import React, { useState, useEffect, useMemo, useCallback } from 'react';
import TopBar from '../components/layout/TopBar';
import { Icon } from '@iconify/react';
import { GlassCard, Button, Badge, Modal, Spinner } from '../components/ui';
import { useToast } from '../context/ToastContext';
import { api } from '../lib/api';

// ─── Types ──────────────────────────────────────────────────

interface AppRole {
  id: string;
  name: string;
  description: string | null;
  color: string;
  icon: string;
  isSystem: boolean;
  isActive: boolean;
  sortOrder: number;
  permissions: { id: string; module: string; action: string }[];
  _count: { users: number };
}

interface UserRecord {
  id: string;
  email: string;
  name: string;
  phone: string | null;
  avatarUrl: string | null;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
  appRoleId: string | null;
  appRole: { id: string; name: string; color: string; icon: string } | null;
  _count: { sales: number; clients: number };
}

type MainTab = 'users' | 'roles';
type ViewMode = 'grid' | 'list';

// ─── Constants ──────────────────────────────────────────────

const ALL_MODULES = [
  { key: 'dashboard', label: 'Dashboard', icon: 'solar:home-2-bold-duotone' },
  { key: 'clients', label: 'Clients', icon: 'solar:user-bold-duotone' },
  { key: 'inventory', label: 'Map Inventory', icon: 'solar:map-bold-duotone' },
  { key: 'sales', label: 'Sales Pipeline', icon: 'solar:sale-bold-duotone' },
  { key: 'payments', label: 'Payments', icon: 'solar:wallet-money-bold-duotone' },
  { key: 'commission_rules', label: 'Commission Rules', icon: 'solar:hand-money-bold-duotone' },
  { key: 'reports', label: 'Reports', icon: 'solar:chart-2-bold-duotone' },
  { key: 'users', label: 'Users & Roles', icon: 'solar:users-group-rounded-bold-duotone' },
  { key: 'settings', label: 'Settings', icon: 'solar:settings-bold-duotone' },
  { key: 'refund_policies', label: 'Refund Policies', icon: 'solar:wallet-money-bold-duotone' },
];

const ALL_ACTIONS = [
  { key: 'view', label: 'View' },
  { key: 'create', label: 'Create' },
  { key: 'edit', label: 'Edit' },
  { key: 'delete', label: 'Delete' },
  { key: 'export', label: 'Export' },
  { key: 'manage', label: 'Manage' },
];

const formatDate = (d: string) => new Date(d).toLocaleDateString('en-PH', { year: 'numeric', month: 'short', day: 'numeric' });

// ─── Shared Styles ──────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '9px 12px', borderRadius: 'var(--radius-md)',
  background: 'var(--input-bg)', border: '1px solid var(--input-border)',
  color: 'var(--input-text)', fontSize: '13px', outline: 'none',
  fontFamily: 'var(--font-body)',
};

const labelStyle: React.CSSProperties = {
  fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)',
  textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: '6px',
};

// ─── Component ──────────────────────────────────────────────

const Users: React.FC = () => {
  const { addToast } = useToast();
  const [mainTab, setMainTab] = useState<MainTab>('users');
  const [loading, setLoading] = useState(true);

  // ─── Users State ──────────────────────────────────────────
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [viewMode, setViewMode] = useState<ViewMode>('list');

  // User Form Modal
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<UserRecord | null>(null);
  const [userForm, setUserForm] = useState({ name: '', email: '', phone: '', password: '', appRoleId: '' });
  const [savingUser, setSavingUser] = useState(false);

  // ─── Roles State ──────────────────────────────────────────
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [selectedRole, setSelectedRole] = useState<AppRole | null>(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [editingRole, setEditingRole] = useState<AppRole | null>(null);
  const [roleForm, setRoleForm] = useState({ name: '', description: '', color: '#3B82F6', icon: 'solar:shield-user-bold-duotone' });
  const [savingRole, setSavingRole] = useState(false);
  const [pendingPermissions, setPendingPermissions] = useState<Record<string, Set<string>>>({});
  const [savingPerms, setSavingPerms] = useState(false);

  // ─── Data Loading ─────────────────────────────────────────

  const loadUsers = useCallback(async () => {
    try {
      const params: Record<string, string> = {};
      if (searchQuery) params.search = searchQuery;
      if (roleFilter !== 'ALL') params.roleId = roleFilter;
      if (statusFilter !== 'ALL') params.status = statusFilter;
      const data = await api.users.list(params);
      setUsers(data.users);
      setTotalUsers(data.total);
    } catch (err: any) {
      addToast('error', 'Error', err.message || 'Failed to load users');
    }
  }, [searchQuery, roleFilter, statusFilter, addToast]);

  const loadRoles = useCallback(async () => {
    try {
      const data = await api.users.roles.list();
      setRoles(data);
      if (!selectedRole && data.length > 0) {
        setSelectedRole(data[0]);
        buildPermMap(data[0]);
      } else if (selectedRole) {
        const updated = data.find((r: AppRole) => r.id === selectedRole.id);
        if (updated) { setSelectedRole(updated); buildPermMap(updated); }
      }
    } catch (err: any) {
      addToast('error', 'Error', err.message || 'Failed to load roles');
    }
  }, [selectedRole, addToast]);

  const buildPermMap = (role: AppRole) => {
    const map: Record<string, Set<string>> = {};
    for (const p of role.permissions) {
      if (!map[p.module]) map[p.module] = new Set();
      map[p.module].add(p.action);
    }
    setPendingPermissions(map);
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([loadUsers(), loadRoles()]);
      setLoading(false);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!loading) loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, roleFilter, statusFilter]);

  // ─── User Handlers ────────────────────────────────────────

  const openCreateUser = () => {
    setEditingUser(null);
    setUserForm({ name: '', email: '', phone: '', password: '', appRoleId: roles.length > 0 ? roles[roles.length - 1].id : '' });
    setShowUserModal(true);
  };

  const openEditUser = (user: UserRecord) => {
    setEditingUser(user);
    setUserForm({ name: user.name, email: user.email, phone: user.phone || '', password: '', appRoleId: user.appRoleId || '' });
    setShowUserModal(true);
  };

  const handleSaveUser = async () => {
    if (!userForm.name || !userForm.email) { addToast('error', 'Validation', 'Name and email are required'); return; }
    if (!editingUser && !userForm.password) { addToast('error', 'Validation', 'Password is required for new users'); return; }

    setSavingUser(true);
    try {
      if (editingUser) {
        const data: any = { name: userForm.name, email: userForm.email, phone: userForm.phone, appRoleId: userForm.appRoleId || null };
        if (userForm.password) data.password = userForm.password;
        await api.users.update(editingUser.id, data);
        addToast('success', 'User Updated', `${userForm.name} has been updated`);
      } else {
        await api.users.create({ ...userForm, appRoleId: userForm.appRoleId || null });
        addToast('success', 'User Created', `${userForm.name} has been added`);
      }
      setShowUserModal(false);
      await loadUsers();
    } catch (err: any) {
      addToast('error', 'Error', err.message || 'Failed to save user');
    }
    setSavingUser(false);
  };

  const handleToggleUserStatus = async (user: UserRecord) => {
    try {
      await api.users.toggleStatus(user.id);
      addToast('success', 'Status Updated', `${user.name} is now ${user.isActive ? 'inactive' : 'active'}`);
      await loadUsers();
    } catch (err: any) {
      addToast('error', 'Error', err.message || 'Failed to toggle status');
    }
  };

  const handleDeleteUser = async (user: UserRecord) => {
    if (!window.confirm(`Delete ${user.name}? This cannot be undone.`)) return;
    try {
      await api.users.delete(user.id);
      addToast('success', 'User Deleted', `${user.name} has been removed`);
      await loadUsers();
    } catch (err: any) {
      addToast('error', 'Error', err.message || 'Failed to delete user');
    }
  };

  // ─── Role Handlers ────────────────────────────────────────

  const openCreateRole = () => {
    setEditingRole(null);
    setRoleForm({ name: '', description: '', color: '#3B82F6', icon: 'solar:shield-user-bold-duotone' });
    setShowRoleModal(true);
  };

  const openEditRole = (role: AppRole) => {
    setEditingRole(role);
    setRoleForm({ name: role.name, description: role.description || '', color: role.color, icon: role.icon });
    setShowRoleModal(true);
  };

  const handleSaveRole = async () => {
    if (!roleForm.name) { addToast('error', 'Validation', 'Role name is required'); return; }
    setSavingRole(true);
    try {
      if (editingRole) {
        await api.users.roles.update(editingRole.id, roleForm);
        addToast('success', 'Role Updated', `${roleForm.name} has been updated`);
      } else {
        await api.users.roles.create(roleForm);
        addToast('success', 'Role Created', `${roleForm.name} has been added`);
      }
      setShowRoleModal(false);
      await loadRoles();
    } catch (err: any) {
      addToast('error', 'Error', err.message || 'Failed to save role');
    }
    setSavingRole(false);
  };

  const handleDeleteRole = async (role: AppRole) => {
    if (!window.confirm(`Delete role "${role.name}"?`)) return;
    try {
      await api.users.roles.delete(role.id);
      addToast('success', 'Role Deleted', `${role.name} has been removed`);
      if (selectedRole?.id === role.id) setSelectedRole(null);
      await loadRoles();
    } catch (err: any) {
      addToast('error', 'Error', err.message || 'Failed to delete role');
    }
  };

  // ─── Permission Handlers ──────────────────────────────────

  const togglePermission = (module: string, action: string) => {
    setPendingPermissions(prev => {
      const next = { ...prev };
      if (!next[module]) next[module] = new Set();
      else next[module] = new Set(next[module]);
      if (next[module].has(action)) next[module].delete(action);
      else next[module].add(action);
      return next;
    });
  };

  const toggleModuleAll = (module: string) => {
    setPendingPermissions(prev => {
      const next = { ...prev };
      const current = next[module] || new Set();
      const allActions = ALL_ACTIONS.map(a => a.key);
      const hasAll = allActions.every(a => current.has(a));
      next[module] = hasAll ? new Set() : new Set(allActions);
      return next;
    });
  };

  const handleSavePermissions = async () => {
    if (!selectedRole) return;
    setSavingPerms(true);
    try {
      const perms: { module: string; action: string }[] = [];
      for (const [mod, actions] of Object.entries(pendingPermissions)) {
        Array.from(actions).forEach(action => {
          perms.push({ module: mod, action });
        });
      }
      await api.users.roles.updatePermissions(selectedRole.id, perms);
      addToast('success', 'Permissions Saved', `Permissions for ${selectedRole.name} updated`);
      await loadRoles();
    } catch (err: any) {
      addToast('error', 'Error', err.message || 'Failed to save permissions');
    }
    setSavingPerms(false);
  };

  // ─── Computed ─────────────────────────────────────────────

  const stats = useMemo(() => ({
    total: totalUsers,
    active: users.filter(u => u.isActive).length,
    inactive: users.filter(u => !u.isActive).length,
    roles: roles.length,
  }), [users, totalUsers, roles]);

  // ─── Render ───────────────────────────────────────────────

  if (loading) {
    return (
      <>
        <TopBar title="Users & Roles" />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
          <Spinner />
        </div>
      </>
    );
  }

  return (
    <>
      <TopBar title="Users & Roles" />
      <div style={{ padding: '28px', display: 'flex', flexDirection: 'column', gap: '20px', flex: 1 }}>

        {/* Stats Row */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
          {[
            { label: 'Total Users', value: stats.total, icon: 'solar:users-group-rounded-bold-duotone', color: '#3B82F6' },
            { label: 'Active', value: stats.active, icon: 'solar:check-circle-bold-duotone', color: '#22C55E' },
            { label: 'Inactive', value: stats.inactive, icon: 'solar:close-circle-bold-duotone', color: '#94A3B8' },
            { label: 'Roles Defined', value: stats.roles, icon: 'solar:shield-user-bold-duotone', color: '#8B5CF6' },
          ].map((s, i) => (
            <GlassCard key={i} style={{ padding: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: '36px', height: '36px', borderRadius: 'var(--radius-md)',
                  background: `${s.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon icon={s.icon} width={18} style={{ color: s.color }} />
                </div>
                <div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{s.label}</div>
                  <div style={{ color: 'var(--text-primary)', fontSize: '16px', fontWeight: 700 }}>{s.value}</div>
                </div>
              </div>
            </GlassCard>
          ))}
        </div>

        {/* Tab Switcher */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: '4px', background: 'rgba(148,163,184,0.06)', borderRadius: 'var(--radius-md)', padding: '3px' }}>
            {([
              { key: 'users' as MainTab, label: 'Users', icon: 'solar:users-group-rounded-bold-duotone' },
              { key: 'roles' as MainTab, label: 'Roles & Permissions', icon: 'solar:shield-user-bold-duotone' },
            ]).map(tab => (
              <button key={tab.key} onClick={() => setMainTab(tab.key)} style={{
                padding: '8px 18px', borderRadius: 'var(--radius-sm)', border: 'none', cursor: 'pointer',
                background: mainTab === tab.key ? 'rgba(13,148,136,0.15)' : 'transparent',
                color: mainTab === tab.key ? 'var(--primary-400)' : 'var(--text-muted)',
                fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px',
                transition: 'all 0.15s',
              }}>
                <Icon icon={tab.icon} width={16} />
                {tab.label}
              </button>
            ))}
          </div>

          {mainTab === 'users' && (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <div style={{ display: 'flex', background: 'rgba(148,163,184,0.08)', borderRadius: 'var(--radius-md)', padding: '3px' }}>
                {(['list', 'grid'] as ViewMode[]).map(mode => (
                  <button key={mode} onClick={() => setViewMode(mode)} style={{
                    padding: '6px 10px', borderRadius: 'var(--radius-sm)', border: 'none', cursor: 'pointer',
                    background: viewMode === mode ? 'rgba(13,148,136,0.20)' : 'transparent',
                    color: viewMode === mode ? 'var(--primary-400)' : 'var(--text-muted)', fontSize: '12px', fontWeight: 600,
                    display: 'flex', alignItems: 'center', gap: '4px',
                  }}>
                    <Icon icon={mode === 'grid' ? 'solar:widget-2-bold-duotone' : 'solar:list-bold-duotone'} width={14} />
                  </button>
                ))}
              </div>
              <Button variant="primary" icon="solar:user-plus-bold-duotone" onClick={openCreateUser}>
                Add User
              </Button>
            </div>
          )}

          {mainTab === 'roles' && (
            <Button variant="primary" icon="solar:shield-plus-bold-duotone" onClick={openCreateRole}>
              Add Role
            </Button>
          )}
        </div>

        {/* ════════════════════════════════════════════════════════
            USERS TAB
           ════════════════════════════════════════════════════════ */}
        {mainTab === 'users' && (
          <>
            {/* Toolbar */}
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <div style={{ position: 'relative', flex: 1, maxWidth: '320px' }}>
                <Icon icon="solar:magnifer-bold-duotone" width={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search users..."
                  style={{ ...inputStyle, paddingLeft: '32px' }} />
              </div>
              <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)}
                style={{ ...inputStyle, width: '180px', cursor: 'pointer' }}>
                <option value="ALL">All Roles</option>
                {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                style={{ ...inputStyle, width: '140px', cursor: 'pointer' }}>
                <option value="ALL">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            {/* List View */}
            {viewMode === 'list' && (
              <GlassCard style={{ padding: '0', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(148,163,184,0.10)' }}>
                      {['User', 'Role', 'Status', 'Sales', 'Clients', 'Last Login', 'Actions'].map(h => (
                        <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontSize: '10px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(user => (
                      <tr key={user.id} style={{ borderBottom: '1px solid rgba(148,163,184,0.06)', transition: 'background 0.15s' }}>
                        <td style={{ padding: '12px 14px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{
                              width: '36px', height: '36px', borderRadius: 'var(--radius-md)',
                              background: user.appRole ? `${user.appRole.color}15` : 'rgba(148,163,184,0.10)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              <Icon icon={user.appRole?.icon || 'solar:user-bold-duotone'} width={18}
                                style={{ color: user.appRole?.color || 'var(--text-muted)' }} />
                            </div>
                            <div>
                              <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '13px' }}>{user.name}</div>
                              <div style={{ color: 'var(--text-faint)', fontSize: '11px' }}>{user.email}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          {user.appRole ? (
                            <span style={{ color: user.appRole.color, fontSize: '12px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <Icon icon={user.appRole.icon} width={13} />
                              {user.appRole.name}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--text-faint)', fontSize: '12px' }}>No role</span>
                          )}
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <Badge variant={user.isActive ? 'available' : 'default' as any}>
                            {user.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td style={{ padding: '12px 14px', color: 'var(--text-primary)', fontSize: '13px', fontWeight: 600 }}>{user._count.sales}</td>
                        <td style={{ padding: '12px 14px', color: 'var(--text-primary)', fontSize: '13px', fontWeight: 600 }}>{user._count.clients}</td>
                        <td style={{ padding: '12px 14px', color: 'var(--text-secondary)', fontSize: '12px' }}>
                          {user.lastLoginAt ? formatDate(user.lastLoginAt) : 'Never'}
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <button onClick={() => openEditUser(user)} title="Edit"
                              style={{ background: 'rgba(148,163,184,0.08)', border: 'none', borderRadius: '4px', padding: '5px', cursor: 'pointer', color: 'var(--text-secondary)' }}>
                              <Icon icon="solar:pen-bold-duotone" width={14} />
                            </button>
                            <button onClick={() => handleToggleUserStatus(user)} title={user.isActive ? 'Deactivate' : 'Activate'}
                              style={{
                                background: user.isActive ? 'rgba(239,68,68,0.08)' : 'rgba(34,197,94,0.08)',
                                border: 'none', borderRadius: '4px', padding: '5px', cursor: 'pointer',
                                color: user.isActive ? '#EF4444' : '#22C55E',
                              }}>
                              <Icon icon={user.isActive ? 'solar:user-block-bold-duotone' : 'solar:user-check-bold-duotone'} width={14} />
                            </button>
                            <button onClick={() => handleDeleteUser(user)} title="Delete"
                              style={{ background: 'rgba(239,68,68,0.08)', border: 'none', borderRadius: '4px', padding: '5px', cursor: 'pointer', color: '#EF4444' }}>
                              <Icon icon="solar:trash-bin-trash-bold-duotone" width={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {users.length === 0 && (
                  <div style={{ padding: '40px', textAlign: 'center' }}>
                    <Icon icon="solar:users-group-rounded-bold-duotone" width={48} style={{ color: 'var(--text-faint)', marginBottom: '12px' }} />
                    <div style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '4px' }}>No users found</div>
                    <div style={{ color: 'var(--text-faint)', fontSize: '12px' }}>Try adjusting your search or filters</div>
                  </div>
                )}
              </GlassCard>
            )}

            {/* Grid View */}
            {viewMode === 'grid' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
                {users.map(user => (
                  <GlassCard key={user.id} style={{ padding: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', marginBottom: '14px' }}>
                      <div style={{
                        width: '48px', height: '48px', borderRadius: 'var(--radius-lg)',
                        background: user.appRole ? `${user.appRole.color}15` : 'rgba(148,163,184,0.10)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        border: `2px solid ${user.appRole?.color || '#94A3B8'}30`,
                      }}>
                        <Icon icon={user.appRole?.icon || 'solar:user-bold-duotone'} width={22}
                          style={{ color: user.appRole?.color || 'var(--text-muted)' }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '15px' }}>{user.name}</span>
                          <div style={{
                            width: '8px', height: '8px', borderRadius: '50%',
                            background: user.isActive ? '#22C55E' : '#94A3B8',
                            boxShadow: `0 0 6px ${user.isActive ? '#22C55E50' : '#94A3B850'}`,
                          }} />
                        </div>
                        <div style={{ color: user.appRole?.color || 'var(--text-muted)', fontSize: '12px', fontWeight: 600 }}>
                          {user.appRole?.name || 'No Role'}
                        </div>
                        <div style={{ color: 'var(--text-faint)', fontSize: '11px' }}>{user.email}</div>
                      </div>
                      <Badge variant={user.isActive ? 'available' : 'default' as any}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '14px' }}>
                      {[
                        { label: 'Sales', value: user._count.sales },
                        { label: 'Clients', value: user._count.clients },
                        { label: 'Phone', value: user.phone || '—' },
                      ].map((m, i) => (
                        <div key={i} style={{ textAlign: 'center', padding: '8px', borderRadius: 'var(--radius-sm)', background: 'rgba(148,163,184,0.05)' }}>
                          <div style={{ color: 'var(--text-muted)', fontSize: '10px', fontWeight: 600, textTransform: 'uppercase' }}>{m.label}</div>
                          <div style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: 600, marginTop: '2px' }}>{m.value}</div>
                        </div>
                      ))}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '12px', borderTop: '1px solid rgba(148,163,184,0.08)' }}>
                      <span style={{ color: 'var(--text-faint)', fontSize: '11px' }}>
                        <Icon icon="solar:calendar-bold-duotone" width={12} style={{ marginRight: '4px' }} />
                        Joined {formatDate(user.createdAt)}
                      </span>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button onClick={() => openEditUser(user)}
                          style={{ background: 'rgba(148,163,184,0.08)', border: 'none', borderRadius: '4px', padding: '4px', cursor: 'pointer', color: 'var(--text-secondary)' }}>
                          <Icon icon="solar:pen-bold-duotone" width={14} />
                        </button>
                        <button onClick={() => handleToggleUserStatus(user)}
                          style={{ background: user.isActive ? 'rgba(239,68,68,0.08)' : 'rgba(34,197,94,0.08)', border: 'none', borderRadius: '4px', padding: '4px', cursor: 'pointer', color: user.isActive ? '#EF4444' : '#22C55E' }}>
                          <Icon icon={user.isActive ? 'solar:user-block-bold-duotone' : 'solar:user-check-bold-duotone'} width={14} />
                        </button>
                      </div>
                    </div>
                  </GlassCard>
                ))}
                {users.length === 0 && (
                  <div style={{ gridColumn: '1 / -1', padding: '40px', textAlign: 'center' }}>
                    <Icon icon="solar:users-group-rounded-bold-duotone" width={48} style={{ color: 'var(--text-faint)', marginBottom: '12px' }} />
                    <div style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>No users found</div>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* ════════════════════════════════════════════════════════
            ROLES & PERMISSIONS TAB
           ════════════════════════════════════════════════════════ */}
        {mainTab === 'roles' && (
          <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '16px', minHeight: '500px' }}>

            {/* Role List (Left Panel) */}
            <GlassCard style={{ padding: '0', overflow: 'hidden' }}>
              <div style={{ padding: '16px', borderBottom: '1px solid rgba(148,163,184,0.08)' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text-primary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  <Icon icon="solar:shield-user-bold-duotone" width={14} style={{ marginRight: '6px', color: 'var(--primary-500)' }} />
                  Roles
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {roles.map(role => (
                  <button key={role.id} onClick={() => { setSelectedRole(role); buildPermMap(role); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px',
                      background: selectedRole?.id === role.id ? 'rgba(13,148,136,0.08)' : 'transparent',
                      border: 'none', borderBottom: '1px solid rgba(148,163,184,0.06)',
                      borderLeft: selectedRole?.id === role.id ? '3px solid var(--primary-500)' : '3px solid transparent',
                      cursor: 'pointer', width: '100%', textAlign: 'left', transition: 'all 0.15s',
                    }}>
                    <div style={{
                      width: '32px', height: '32px', borderRadius: 'var(--radius-sm)',
                      background: `${role.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Icon icon={role.icon} width={16} style={{ color: role.color }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {role.name}
                        {role.isSystem && (
                          <Icon icon="solar:lock-bold-duotone" width={10} style={{ color: 'var(--text-faint)' }} />
                        )}
                      </div>
                      <div style={{ color: 'var(--text-faint)', fontSize: '10px' }}>
                        {role._count.users} user{role._count.users !== 1 ? 's' : ''} · {role.permissions.length} perms
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </GlassCard>

            {/* Permission Matrix (Right Panel) */}
            <GlassCard style={{ padding: '0', overflow: 'hidden' }}>
              {selectedRole ? (
                <>
                  {/* Role Header */}
                  <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(148,163,184,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '40px', height: '40px', borderRadius: 'var(--radius-md)',
                        background: `${selectedRole.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        border: `2px solid ${selectedRole.color}25`,
                      }}>
                        <Icon icon={selectedRole.icon} width={20} style={{ color: selectedRole.color }} />
                      </div>
                      <div>
                        <div style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: '16px', fontFamily: 'var(--font-heading)' }}>
                          {selectedRole.name}
                        </div>
                        <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                          {selectedRole.description || 'No description'}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button onClick={() => openEditRole(selectedRole)} title="Edit Role"
                        style={{ background: 'rgba(148,163,184,0.08)', border: 'none', borderRadius: 'var(--radius-sm)', padding: '6px 10px', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '11px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Icon icon="solar:pen-bold-duotone" width={13} /> Edit
                      </button>
                      {!selectedRole.isSystem && (
                        <button onClick={() => handleDeleteRole(selectedRole)} title="Delete Role"
                          style={{ background: 'rgba(239,68,68,0.08)', border: 'none', borderRadius: 'var(--radius-sm)', padding: '6px 10px', cursor: 'pointer', color: '#EF4444', fontSize: '11px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Icon icon="solar:trash-bin-trash-bold-duotone" width={13} /> Delete
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Permission Grid */}
                  <div style={{ padding: '16px 20px', overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '10px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', width: '200px' }}>Module</th>
                          {ALL_ACTIONS.map(a => (
                            <th key={a.key} style={{ padding: '8px 10px', textAlign: 'center', fontSize: '10px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{a.label}</th>
                          ))}
                          <th style={{ padding: '8px 10px', textAlign: 'center', fontSize: '10px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>All</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ALL_MODULES.map((mod, idx) => {
                          const modPerms = pendingPermissions[mod.key] || new Set<string>();
                          const allChecked = ALL_ACTIONS.every(a => modPerms.has(a.key));
                          return (
                            <tr key={mod.key} style={{ borderTop: idx > 0 ? '1px solid rgba(148,163,184,0.06)' : undefined }}>
                              <td style={{ padding: '10px 12px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <Icon icon={mod.icon} width={15} style={{ color: 'var(--text-muted)' }} />
                                  <span style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: 500 }}>{mod.label}</span>
                                </div>
                              </td>
                              {ALL_ACTIONS.map(action => {
                                const checked = modPerms.has(action.key);
                                return (
                                  <td key={action.key} style={{ padding: '10px', textAlign: 'center' }}>
                                    <button
                                      onClick={() => togglePermission(mod.key, action.key)}
                                      style={{
                                        width: '28px', height: '28px', borderRadius: 'var(--radius-sm)',
                                        border: checked ? '2px solid var(--primary-500)' : '2px solid rgba(148,163,184,0.20)',
                                        background: checked ? 'rgba(13,148,136,0.15)' : 'transparent',
                                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        transition: 'all 0.15s',
                                      }}>
                                      {checked && <Icon icon="solar:check-read-bold" width={14} style={{ color: 'var(--primary-500)' }} />}
                                    </button>
                                  </td>
                                );
                              })}
                              <td style={{ padding: '10px', textAlign: 'center' }}>
                                <button
                                  onClick={() => toggleModuleAll(mod.key)}
                                  style={{
                                    width: '28px', height: '28px', borderRadius: 'var(--radius-sm)',
                                    border: allChecked ? '2px solid #8B5CF6' : '2px solid rgba(148,163,184,0.20)',
                                    background: allChecked ? 'rgba(139,92,246,0.15)' : 'transparent',
                                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    transition: 'all 0.15s',
                                  }}>
                                  {allChecked && <Icon icon="solar:check-read-bold" width={14} style={{ color: '#8B5CF6' }} />}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Save Permissions Button */}
                  <div style={{ padding: '12px 20px', borderTop: '1px solid rgba(148,163,184,0.08)', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                    <Button variant="secondary" onClick={() => buildPermMap(selectedRole)}>
                      Reset
                    </Button>
                    <Button variant="primary" icon={savingPerms ? undefined : 'solar:diskette-bold-duotone'} onClick={handleSavePermissions}>
                      {savingPerms ? 'Saving...' : 'Save Permissions'}
                    </Button>
                  </div>
                </>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', padding: '40px' }}>
                  <div style={{ textAlign: 'center' }}>
                    <Icon icon="solar:shield-user-bold-duotone" width={48} style={{ color: 'var(--text-faint)', marginBottom: '12px' }} />
                    <div style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>Select a role to manage permissions</div>
                  </div>
                </div>
              )}
            </GlassCard>
          </div>
        )}
      </div>

      {/* ─── User Create/Edit Modal ─── */}
      <Modal
        isOpen={showUserModal}
        onClose={() => setShowUserModal(false)}
        title={editingUser ? `Edit ${editingUser.name}` : 'Add New User'}
        width="500px"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowUserModal(false)}>Cancel</Button>
            <Button variant="primary" icon={savingUser ? undefined : 'solar:diskette-bold-duotone'} onClick={handleSaveUser}>
              {savingUser ? 'Saving...' : (editingUser ? 'Update User' : 'Create User')}
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={labelStyle}>Full Name *</label>
            <input value={userForm.name} onChange={e => setUserForm(p => ({ ...p, name: e.target.value }))}
              placeholder="Enter full name" style={inputStyle} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={labelStyle}>Email *</label>
              <input type="email" value={userForm.email} onChange={e => setUserForm(p => ({ ...p, email: e.target.value }))}
                placeholder="user@email.com" style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Phone</label>
              <input value={userForm.phone} onChange={e => setUserForm(p => ({ ...p, phone: e.target.value }))}
                placeholder="+63 9xx xxx xxxx" style={inputStyle} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={labelStyle}>Role *</label>
              <select value={userForm.appRoleId} onChange={e => setUserForm(p => ({ ...p, appRoleId: e.target.value }))}
                style={{ ...inputStyle, cursor: 'pointer' }}>
                <option value="">No Role</option>
                {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>{editingUser ? 'New Password (leave blank to keep)' : 'Password *'}</label>
              <input type="password" value={userForm.password} onChange={e => setUserForm(p => ({ ...p, password: e.target.value }))}
                placeholder={editingUser ? '••••••••' : 'Min 6 characters'} style={inputStyle} />
            </div>
          </div>
        </div>
      </Modal>

      {/* ─── Role Create/Edit Modal ─── */}
      <Modal
        isOpen={showRoleModal}
        onClose={() => setShowRoleModal(false)}
        title={editingRole ? `Edit ${editingRole.name}` : 'Create New Role'}
        width="450px"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowRoleModal(false)}>Cancel</Button>
            <Button variant="primary" icon={savingRole ? undefined : 'solar:diskette-bold-duotone'} onClick={handleSaveRole}>
              {savingRole ? 'Saving...' : (editingRole ? 'Update Role' : 'Create Role')}
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={labelStyle}>Role Name *</label>
            <input value={roleForm.name} onChange={e => setRoleForm(p => ({ ...p, name: e.target.value }))}
              placeholder="e.g. Branch Manager" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Description</label>
            <input value={roleForm.description} onChange={e => setRoleForm(p => ({ ...p, description: e.target.value }))}
              placeholder="What can this role do?" style={inputStyle} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={labelStyle}>Color</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input type="color" value={roleForm.color} onChange={e => setRoleForm(p => ({ ...p, color: e.target.value }))}
                  style={{ width: '36px', height: '36px', border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer', padding: 0 }} />
                <input value={roleForm.color} onChange={e => setRoleForm(p => ({ ...p, color: e.target.value }))}
                  style={{ ...inputStyle, flex: 1, fontFamily: 'monospace' }} />
              </div>
            </div>
            <div>
              <label style={labelStyle}>Icon</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{
                  width: '36px', height: '36px', borderRadius: 'var(--radius-sm)',
                  background: `${roleForm.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon icon={roleForm.icon} width={18} style={{ color: roleForm.color }} />
                </div>
                <input value={roleForm.icon} onChange={e => setRoleForm(p => ({ ...p, icon: e.target.value }))}
                  placeholder="solar:icon-name" style={{ ...inputStyle, flex: 1, fontSize: '11px' }} />
              </div>
            </div>
          </div>
          {/* Quick icon picker */}
          <div>
            <label style={labelStyle}>Quick Icons</label>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {[
                'solar:shield-user-bold-duotone',
                'solar:crown-bold-duotone',
                'solar:shield-check-bold-duotone',
                'solar:user-bold-duotone',
                'solar:wallet-money-bold-duotone',
                'solar:users-group-rounded-bold-duotone',
                'solar:star-bold-duotone',
                'solar:key-bold-duotone',
                'solar:diploma-bold-duotone',
                'solar:briefcase-bold-duotone',
              ].map(ic => (
                <button key={ic} onClick={() => setRoleForm(p => ({ ...p, icon: ic }))}
                  style={{
                    width: '32px', height: '32px', borderRadius: 'var(--radius-sm)',
                    border: roleForm.icon === ic ? `2px solid ${roleForm.color}` : '1px solid rgba(148,163,184,0.15)',
                    background: roleForm.icon === ic ? `${roleForm.color}15` : 'rgba(148,163,184,0.05)',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                  <Icon icon={ic} width={16} style={{ color: roleForm.icon === ic ? roleForm.color : 'var(--text-muted)' }} />
                </button>
              ))}
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default Users;
